import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { Search, Phone, Mail, MapPin, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { motion } from 'framer-motion';

const Representatives = () => {
  const [states, setStates] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [filteredReps, setFilteredReps] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchStates();
    fetchAllRepresentatives();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchConstituencies(selectedState);
      setSelectedConstituency('');
    }
  }, [selectedState]);

  useEffect(() => {
    filterRepresentatives();
  }, [selectedState, selectedConstituency, searchQuery, representatives]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/representatives/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchConstituencies = async (state) => {
    try {
      const response = await axios.get(`${API}/representatives/constituencies/${state}`);
      setConstituencies(response.data.constituencies);
    } catch (error) {
      console.error('Error fetching constituencies:', error);
    }
  };

  const fetchAllRepresentatives = async () => {
    try {
      const response = await axios.get(`${API}/representatives?limit=500`);
      setRepresentatives(response.data);
      setFilteredReps(response.data);
    } catch (error) {
      console.error('Error fetching representatives:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRepresentatives = () => {
    let filtered = representatives;

    if (selectedState) {
      filtered = filtered.filter(rep => rep.state === selectedState);
    }

    if (selectedConstituency) {
      filtered = filtered.filter(rep => rep.constituency === selectedConstituency);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(rep => 
        rep.name.toLowerCase().includes(query) ||
        rep.state.toLowerCase().includes(query) ||
        rep.constituency.toLowerCase().includes(query)
      );
    }

    setFilteredReps(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleReset = () => {
    setSelectedState('');
    setSelectedConstituency('');
    setSearchQuery('');
    setFilteredReps(representatives);
    setCurrentPage(1);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredReps.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReps = filteredReps.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
            Find Your Representative
          </h1>
          <p className="text-lg text-slate-600">
            Locate your federal representative by state and constituency. Connect with them via phone or email.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select State
              </label>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Choose a state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state} value={state}>
                      {state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Constituency
              </label>
              <Select 
                value={selectedConstituency} 
                onValueChange={setSelectedConstituency}
                disabled={!selectedState}
              >
                <SelectTrigger data-testid="constituency-select">
                  <SelectValue placeholder="Choose constituency" />
                </SelectTrigger>
                <SelectContent>
                  {constituencies.map((constituency) => (
                    <SelectItem key={constituency} value={constituency}>
                      {constituency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Search by Name
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Enter representative name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full pl-10 h-10 rounded-lg border border-slate-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredReps.length)} of {filteredReps.length} representative{filteredReps.length !== 1 ? 's' : ''}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </p>
            <Button 
              variant="outline" 
              onClick={handleReset}
              data-testid="reset-button"
            >
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Representatives Grid */}
        {filteredReps.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600 text-lg">No representatives found matching your criteria.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentReps.map((rep, index) => (
              <motion.div
                key={rep.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all overflow-hidden group"
                data-testid={`rep-card-${index}`}
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-16 h-16 bg-primary-700 rounded-full flex items-center justify-center text-white font-heading font-semibold text-xl flex-shrink-0">
                      {getInitials(rep.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1 truncate">
                        {rep.name}
                      </h3>
                      <p className="text-sm text-slate-600">{rep.state}</p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-start space-x-2 text-sm">
                      <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{rep.constituency}</span>
                    </div>
                    {rep.office_no && (
                      <div className="flex items-center space-x-2 text-sm">
                        <div className="h-4 w-4 flex items-center justify-center">
                          <span className="text-xs text-slate-400">💼</span>
                        </div>
                        <span className="text-slate-600">Office {rep.office_no}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {rep.phone && (
                      <a 
                        href={`tel:${rep.phone}`}
                        className="flex items-center space-x-2 text-sm text-primary-700 hover:text-primary-900 transition-colors"
                        data-testid="call-button"
                      >
                        <Phone className="h-4 w-4" />
                        <span>{rep.phone}</span>
                      </a>
                    )}
                    {rep.email && (
                      <a 
                        href={`mailto:${rep.email}`}
                        className="flex items-center space-x-2 text-sm text-primary-700 hover:text-primary-900 transition-colors truncate"
                        data-testid="email-button"
                      >
                        <Mail className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{rep.email}</span>
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-8">
              <Button
                variant="outline"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="flex items-center space-x-1"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </Button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      onClick={() => goToPage(pageNum)}
                      className="w-10 h-10 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="flex items-center space-x-1"
              >
                <span>Next</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
        )}
      </div>
    </div>
  );
};

export default Representatives;
